import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const STORAGE_KEY = "ici_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) { /* ignore */ }
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((p) => p.product_id === product.product_id);
      if (found) {
        return prev.map((p) =>
          p.product_id === product.product_id ? { ...p, quantity: p.quantity + qty } : p
        );
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          title: product.title,
          price: product.price,
          image: product.main_image,
          sku: product.sku,
          slug: product.slug,
          quantity: qty,
        },
      ];
    });
    toast.success("Added to cart", { description: product.title.slice(0, 60) });
  }, []);

  const remove = useCallback((pid) => {
    setItems((prev) => prev.filter((p) => p.product_id !== pid));
  }, []);

  const updateQty = useCallback((pid, qty) => {
    if (qty <= 0) return remove(pid);
    setItems((prev) => prev.map((p) => (p.product_id === pid ? { ...p, quantity: qty } : p)));
  }, [remove]);

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, updateQty, clear, open, setOpen, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
